import React, { useState, useEffect, useContext } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { useTranslator } from '@jutro/locale';
import { MLRiskAnalysisService } from 'ml-services-common-react';
import { FormModalPopover, platformMessages } from 'ml-components-platform-react';
import { DateValue, CurrencyValue, useModal } from '@jutro/components';

import metadata from './MLRatingFactorConsumerReportsComponent.metadata.json5';
import messages from './MLRatingFactorConsumerReportsComponent.messages';

import MLRatingFactorConsumerReportsClaimDetailsPopupMetadata from './MLRatingFactorConsumerReportsClaimDetailsPopup.metadata.json5';
import appConfig from 'app-config';
import { useAuthentication } from '@xengage/gw-digital-auth-react';

const MLRatingFactorConsumerReportsComponent = (props) => {
    const modalApi = useModal();
    const { jobOrPolicyNumber, isQuote, isPolicy, isPersonalAuto, isPersonalUmbrella, isHomeOwners, styles, state, authHeader, userTypeCode, isModernProduct} = props;

    const translator = useTranslator();
    const viewModelService = useContext(ViewModelServiceContext);
    const ratingTypeList = viewModelService?.productMetadata?.get('pc')?.types?.getTypelist('IncidentUsedInRating_ML') || []
    

    const [priorLosses, setPriorLosses] = useState([]);
    const [mvrReports, setMvrReports] = useState([]);
    const [pfmReports, setPfmReports] = useState([]);

    const { authUserData } = useAuthentication();
    const { MLUserTypes } = appConfig;

    const updateConsumerReports = (data = {}) => {
        if (isPersonalAuto) {
            setPriorLosses(data?.lobs?.personalAuto?.priorLosses ?? []);
            setMvrReports(data?.lobs?.personalAuto?.mvrReports ?? []);
        } else if (isHomeOwners) {
            setPriorLosses(data?.lobs?.homeowners?.priorLosses ?? []);
        } else if (isPersonalUmbrella) {
            setPriorLosses(data?.lobs?.personalUmbrella?.priorLosses ?? []);
            setMvrReports(data?.lobs?.personalUmbrella?.mvrReports ?? []);
        }

        setPfmReports(data?.pfmReports ?? []);
    };

    const getAndSetConsumerReportsBySubmissionNumber = (submissionNumber) => {
        if (_.isEmpty(priorLosses)) {
            const promise = MLRiskAnalysisService.getRAConsumerReportsMLByJobNumber(submissionNumber, authHeader);
            promise.then((data) => {
                updateConsumerReports(data);
            });
        }
    };

    const getAndSetConsumerReportsByPolicyNumber = (policyNumber) => {
        const promise = MLRiskAnalysisService.getRAConsumerReportsMLByPolicyNumber(policyNumber, authHeader);
        promise.then((data) => {
            updateConsumerReports(data);
        });
    };

    const openClaimDetails = (loss) => {   
        loss.isAutoClaim = loss.claimLob === 'Auto';
        loss.isHomeClaim = loss.claimLob === 'Home';

        const getAutoLossDetailsTableOverrides = () => {
            const getCellAutoLossDetailsHeader = (rowData) => {
                return <strong>{translator(rowData.header)}</strong>;
            };
    
            const getCellAutoLossDetailsValue = (rowData) => {
                return rowData.value;
            };
    
            return {
                autoLossDetailsHeaderColumn: {
                    renderCell: getCellAutoLossDetailsHeader
                },
                autoLossDetailsValueColumn: {
                    renderCell: getCellAutoLossDetailsValue
                }
            };
        };
    
        const getAutoLossDetailsCoveragesTableOverrides = () => {
            const getCellType = (rowData) => {
                return rowData.type;
            };
    
            const getCellAmountPaid = (rowData) => {
                return rowData.amountPaid
                    ? <CurrencyValue showFractions currency={rowData.amountPaid.currency} value={rowData.amountPaid.amount} />
                    : undefined;
            };
    
            const getCellStatus = (rowData) => {
                return rowData.status;
            };
    
            return {
                autoLossDetailsCoverageTypeColumn: {
                    renderCell: getCellType
                },
                autoLossDetailsCoverageAmountPaidColumn: {
                    renderCell: getCellAmountPaid
                },
                autoLossDetailsCoverageStatusColumn: {
                    renderCell: getCellStatus
                }
            };
        };
    
        const getHomeLossDetailsOverrides = (lossDetails) => {
            const getCellHomeLossDetailsHeader = (rowData) => {
                return translator(rowData.header);
            };
    
            const getCellHomeLossDetailsValue = (rowData) => {
                return rowData.value;
            };
    
            const formatHomeLossDetailsData = (details) => {
                return [
                    {
                        header: messages.name,
                        value: details.name
                    },
                    {
                        header: messages.description,
                        value: details.description
                    },
                    {
                        header: messages.actOfGod,
                        value: details.actOfGod
                    },
                    {
                        header: messages.catIndicator,
                        value: details.catastropheInd
                    },
                    {
                        header: messages.COVERAGE_TYPE,
                        value: details.perilCategory
                    },
                    {
                        header: messages.AMOUNT_PAID,
                        value: details.amountPaid
                            ? <CurrencyValue showFractions currency={details.amountPaid.currency} value={details.amountPaid.amount} />
                            : undefined,
                    },
                    {
                        header: messages.STATUS,
                        value: details.status
                    }
                ];
            };
    
            const overrides = lossDetails.map((details, index) => {
                return {
                    [`homeLossDetailsTable${index}`]: {
                        data: formatHomeLossDetailsData(details),
                    },
                    [`homeLossDetailsHeaderColumn${index}`]: {
                        renderCell: getCellHomeLossDetailsHeader
                    },
                    [`homeLossDetailsValueColumn${index}`]: {
                        renderCell: getCellHomeLossDetailsValue
                    }
                }
            });
    
            return Object.assign({}, ...overrides);
        };

        const formatAutoLossDetailsData = (lossDetails) => {
            return [
                {
                    header: messages.driversName,
                    value: lossDetails.driverName
                },
                {
                    header: messages.dateOfBirth,
                    value: <DateValue displayFormat="vshort" value={lossDetails.dateOfBirth} />
                },
                {
                    header: messages.gender,
                    value: lossDetails.gender
                },
                {
                    header: messages.licenseNumber,
                    value: lossDetails.licenseNumber
                },
                {
                    header: messages.licenseState,
                    value: lossDetails.licenseState
                },
                {
                    header: messages.year,
                    value: _.get(lossDetails, 'vehicle.year')
                },
                {
                    header: messages.make,
                    value: _.get(lossDetails, 'vehicle.make')
                },
                {
                    header: messages.model,
                    value: _.get(lossDetails, 'vehicle.model')
                },
                {
                    header: messages.vin,
                    value: _.get(lossDetails, 'vehicle.vin')
                },
                {
                    header: messages.vehicleDisposition,
                    value: _.get(lossDetails, 'vehicle.disposition')
                }
            ];
        };

        let overrides = {
            autoLossDetailsContainer: {
                visible: loss.isAutoClaim
            },
            homeLossDetailsContainer: {
                visible: loss.isHomeClaim
            }
        };

        if (loss.isAutoClaim) {
            overrides = {
                ...overrides,
                autoLossDetailsTable: {
                    data: formatAutoLossDetailsData(loss.lossDetails)
                },
                autoLossDetailsCoveragesTable: {
                    data: loss.lossDetails.coverages
                },
                ...getAutoLossDetailsTableOverrides(),
                ...getAutoLossDetailsCoveragesTableOverrides()
            };
        } else if (loss.isHomeClaim) {
            overrides = {
                ...overrides,
                homeLossDetailsIterable: {
                    data: loss.lossDetails
                },
                ...getHomeLossDetailsOverrides(loss.lossDetails)
            }
        }

        const componentProps = {
            title: messages.claimDetails,
            uiProps: MLRatingFactorConsumerReportsClaimDetailsPopupMetadata.pageContent,
            modalBodyStyles: styles,
            getOverrideProps: () => {
                return overrides;
            }
        };

        modalApi.showModal(
            <FormModalPopover {...componentProps} />
        );
    };

    useEffect(() => {
        if (isQuote) {
            getAndSetConsumerReportsBySubmissionNumber(jobOrPolicyNumber);
        } else if (isPolicy) {
            getAndSetConsumerReportsByPolicyNumber(jobOrPolicyNumber);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getCellPoint = () => {            
        if(state === 'MN') {
            return true
        }
        return false            
    };

    const getPfmReportsTableOverrides = () => {
        const getCellName = (rowData) => {
            return rowData.name;
        };
    
        const getCellGeneratedNumber = (rowData) => {
            return rowData.generatedNumber;
        };
    
        const getCellLevel = (rowData) => {
            return rowData.level;
        };
    
        const getCellOrderDate  = (rowData) => {
            return (
                <DateValue
                    displayFormat="vshort"
                    value={rowData.orderDate}
                />
            );
        };
        
        const getCellReasons  = (rowData) => {
            return rowData.reasons;
        };

        const getCellReportStatus  = (rowData) => {
            return rowData.reportStatus;
        };

        const getCellSource  = (rowData) => {
            return rowData.source;
        };

        const getCellRating  = (rowData) => {
            if (isModernProduct) {
                return rowData.ratingLevel;
            }

            return rowData.hasRating === true ? translator(platformMessages.mlYes) : ''
        };

        const getHeaderRating  = () => {
            if (isModernProduct) {
                return translator(messages.RATINGLEVEL);
            }

            return translator(messages.RATING);
        };
        const isModernProductUmbrella=isPersonalUmbrella && isModernProduct
        
        return {
            pfmReportsContainer:{
             visible:!isModernProductUmbrella   

            },
            pfmReportsTable: {
                data: pfmReports,
                title: isModernProduct ? messages.insuranceScore : messages.pfmReport
            },
            pfmReportsEmpty: {
                visible: _.isEmpty(pfmReports)
            },
            nameColumn: {
                renderCell: getCellName
            },
            generatedNumberColumn: {
                renderCell: getCellGeneratedNumber
            },
            levelColumn: {
                renderCell: getCellLevel
            },
            pfmOrderDateColumn: {
                renderCell: getCellOrderDate
            },
            reasonsColumn: {
                visible : !isModernProduct && !(authUserData.userType === MLUserTypes.IA || authUserData.userType === MLUserTypes.GCS_SALES || authUserData.userType === MLUserTypes.PCS || authUserData.userType === MLUserTypes.MET_GA),
                renderCell: getCellReasons
            },
            reportStatusColumn: {
                visible : isModernProduct,
                renderCell: getCellReportStatus
            },
            sourceColumn: {
                renderCell: getCellSource
            },
            ratingColumn: {
                renderCell: getCellRating,
                header: getHeaderRating,
                visible:!!isModernProduct
            }
        }
    };

    const getPriorLossesPATableOverrides = () => {
        const getCellOrderDate = (rowData) => {
            return <DateValue displayFormat="vshort" value={rowData.orderDate} />;
        };
    
        const getCellClaimDate = (rowData) => {
            return (
                // eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                <a onClick={() => openClaimDetails(rowData)}>
                    <DateValue displayFormat="vshort" tag="span" value={rowData.claimDate} />
                </a>
            );
        };
    
        const getCellAmountPaid = (rowData) => {
            return rowData.amountPaid
                ? <CurrencyValue showFractions currency={rowData.amountPaid.currency} value={rowData.amountPaid.amount} />
                : undefined;        
        };
    
        const getCellVehicleOperator  = (rowData) => {
            return rowData.vehicleOperator;
        };
        
        const getCellFault  = (rowData) => {
            return rowData.fault;
        };

        const getCellDescription  = (rowData) => {
            return rowData.description;
        };

        const getCellUsedInRating  = (rowData) => {
            if(_.isEmpty(rowData?.usedInRating)){
                return ""
            }
            const ratingMeta = ratingTypeList?.getCode(rowData?.usedInRating)
            const ratingMetadata = _.find(ratingMeta?.typelist?.codes, (rating) => {
                return rating.code === rowData?.usedInRating;
            });
            return translator({
                id: ratingMetadata?.name,
                defaultMessage:  ratingMetadata?.name
            })
        }

        return {
            priorLossesPAContainer: {
                visible: isPersonalAuto
            },
            priorLossesPATable: {
                data: priorLosses,
                title: messages.priorLosses
            },
            priorLossesPAEmpty: {
                visible: _.isEmpty(priorLosses)
            },
            paOrderDateColumn: {
                renderCell: getCellOrderDate
            },
            paClaimDateColumn: {
                renderCell: getCellClaimDate
            },
            paAmountPaidColumn: {
                renderCell: getCellAmountPaid
            },
            paVehicleOperatorColumn: {
                renderCell: getCellVehicleOperator
            },
            paFaultColumn: {
                renderCell: getCellFault
            },
            paAutoPointsColumn: {
                visible: getCellPoint()
            },
            paDescriptionColumn: {
                renderCell: getCellDescription
            },
            paUsedInRatingColumn: {
                renderCell: getCellUsedInRating,
                visible: isModernProduct
            }
        }
    };

    const getPriorLossesHOTableOverrides = () => {
        const getCellOrderDate = (rowData) => {
            return (
                <DateValue
                    displayFormat="vshort"
                    value={rowData.orderDate}
                />
            );
        };
    
        const getCellClaimDate = (rowData) => {
            return (
                // eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                <a onClick={() => openClaimDetails(rowData)}>
                    <DateValue displayFormat="vshort" tag="span" value={rowData.claimDate} />
                </a>
            );
        };
    
        const getCellAmountPaid = (rowData) => {
            return rowData.amountPaid
                ? <CurrencyValue showFractions currency={rowData.amountPaid.currency} value={rowData.amountPaid.amount} />
                : undefined;
        };
    
        const getCellInsuredLoss  = (rowData) => {
            return rowData.insuredLoss;
        };
        
        const getCellLossType  = (rowData) => {
            return rowData.lossType;
        };

        return {
            priorLossesHOContainer: {
                visible: isHomeOwners
            },
            priorLossesHOTable: {
                data: priorLosses,
                title: messages.priorLosses
            },
            priorLossesHOEmpty: {
                visible: _.isEmpty(priorLosses)
            },
            hoOrderDateColumn: {
                renderCell: getCellOrderDate
            },
            hoClaimDateColumn: {
                renderCell: getCellClaimDate
            },
            hoAmountPaidColumn: {
                renderCell: getCellAmountPaid
            },
            hoInsuredLossColumn: {
                renderCell: getCellInsuredLoss
            },
            hoLossTypeColumn: {
                renderCell: getCellLossType
            }
        }
    };

    const getPriorLossesPUPTableOverrides = () => {
        const getCellClaimLob = (rowData) => {
            return rowData.claimLob
        };

        const getCellOrderDate = (rowData) => {
            return <DateValue displayFormat="vshort" value={rowData.orderDate} />;
        };
    
        const getCellClaimDate = (rowData) => {
            return (
                // eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                <a onClick={() => openClaimDetails(rowData)}>
                    <DateValue displayFormat="vshort" tag="span" value={rowData.claimDate} />
                </a>
            );
        };
    
        const getCellAmountPaid = (rowData) => {
            return rowData.amountPaid
                ? <CurrencyValue showFractions currency={rowData.amountPaid.currency} value={rowData.amountPaid.amount} />
                : undefined;
        };
    
        const getCellActOfGod  = (rowData) => {
            return rowData.actOfGod;
        };

        const getCellCat  = (rowData) => {
            return rowData.cat;
        };

        const getCellInsuredLoss  = (rowData) => {
            return rowData.insuredLoss;
        };
        
        const getCellTypeOfLoss  = (rowData) => {
            return rowData.typeOfLoss;
        };

        return {
            priorLossesPUPContainer: {
                visible: isPersonalUmbrella
            },
            priorLossesPUPTable: {
                data: priorLosses,
                title: messages.priorLosses
            },
            priorLossesPUPEmpty: {
                visible: _.isEmpty(priorLosses)
            },
            pupClaimLobColumn: {
                renderCell: getCellClaimLob
            },
            pupOrderDateColumn: {
                renderCell: getCellOrderDate
            },
            pupClaimDateColumn: {
                renderCell: getCellClaimDate
            },
            pupAmountPaidColumn: {
                renderCell: getCellAmountPaid
            },
            pupActOfGodColumn: {
                renderCell: getCellActOfGod
            },
            pupCatColumn: {
                renderCell: getCellCat
            },
            pupInsuredLossColumn: {
                renderCell: getCellInsuredLoss
            },
            pupTypeOfLossColumn: {
                renderCell: getCellTypeOfLoss
            }
        }
    }

    const getMvrReportsOverrides = () => {
        const getCellGender = (rowData) => {
            return rowData.gender;
        };

        const getCellDateOfBirth = (rowData) => {
            return <DateValue displayFormat="vshort" value={rowData.dateOfBirth} />;
        };

        const getCellState = (rowData) => {
            return rowData.state;
        };

        const getCellLicenseStatus = (rowData) => {
            return rowData.licenseStatus;
        };

        const getCellOrderDate = (rowData) => {
            return <DateValue displayFormat="vshort" value={rowData.orderDate} />;
        };

        const getCellIncidentsCount = (rowData) => {
            return rowData.incidents.length;
        };

        const getCellIncidentDate = (rowData) => {
            return <DateValue displayFormat="vshort" value={rowData.incidentDate} />;
        };

        const getCellConvictionDate = (rowData) => {
            return <DateValue displayFormat="vshort" value={rowData.convictionDate} />;
        };

        const getCellIncidentType = (rowData) => {
            return rowData.incidentType;
        };

        const getCellDescription = (rowData) => {
            return rowData.description;
        };

        const getCellNoInfo = () => {
            return messages.noInfo
        }

        const overrides = mvrReports.map((report, index) => {
            return {
                [`reportDisplayName${index}`]: {
                    content: report.displayName
                },
                [`mvrReportTable${index}`]: {
                    data: [report]
                },
                [`genderColumn${index}`]: {
                    renderCell: getCellGender
                },
                [`dateOfBirthColumn${index}`]: {
                    renderCell: getCellDateOfBirth
                },
                [`stateColumn${index}`]: {
                    renderCell: getCellState
                },
                [`licenseStatusColumn${index}`]: {
                    renderCell: getCellLicenseStatus
                },
                [`mvrOrderDateColumn${index}`]: {
                    renderCell: getCellOrderDate
                },
                [`incidentsCountColumn${index}`]: {
                    renderCell: getCellIncidentsCount
                },
                [`mvrReportIncidentsTable${index}`]: {
                    data: !_.isEmpty(report.incidents) ? report.incidents : [{}]
                },
                [`incidentDateColumn${index}`]: {
                    renderCell: !_.isEmpty(report.incidents) ? getCellIncidentDate : getCellNoInfo
                },
                [`convictionDateColumn${index}`]: {
                    renderCell: !_.isEmpty(report.incidents) ? getCellConvictionDate : undefined
                },
                [`incidentTypeColumn${index}`]: {
                    renderCell: getCellIncidentType
                },
                [`paAutoPointsColumn${index}`]: {
                    visible: getCellPoint()
                },
                [`incidentDescriptionColumn${index}`]: {
                    renderCell: getCellDescription
                }
            }
        });

        return Object.assign({
            mvrReportsContainer: {
                visible: !isHomeOwners
            },
            mvrReportsEmpty: {
                visible: _.isEmpty(mvrReports)
            }
        }, ...overrides);
    }

    const overrideProps = {
        mvrReportsIterable: {
            data: mvrReports
        },
        ...getPfmReportsTableOverrides(),
        ...getPriorLossesPATableOverrides(),
        ...getPriorLossesHOTableOverrides(),
        ...getPriorLossesPUPTableOverrides(),
        ...getMvrReportsOverrides()
    }

    const resolvers = {
        resolveClassNameMap: styles
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            classNameMap={resolvers.resolveClassNameMap}
        />    
    )
};

MLRatingFactorConsumerReportsComponent.propTypes = {
    jobOrPolicyNumber: PropTypes.string.isRequired,
    isQuote: PropTypes.bool.isRequired,
    isPolicy: PropTypes.bool.isRequired,
    isPersonalAuto: PropTypes.bool,
    isPersonalUmbrella: PropTypes.bool,
    isHomeOwners: PropTypes.bool,
    styles: PropTypes.arrayOf(PropTypes.shape({}))
};

MLRatingFactorConsumerReportsComponent.defaultProps = {
    isPersonalAuto: false,
    isPersonalUmbrella: false,
    isHomeOwners: false,
    styles: undefined
}

export default MLRatingFactorConsumerReportsComponent;